import { computed, reaction } from 'mobx'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { useEditorView } from 'common/Prose/EditorProvider/hooks/useEditorView'
import { Chip } from 'components/Chip/Chip'
import { CollabItemsFilter } from 'components/CollabItemsFilter/CollabItemsFilter'
import { useCommentArticleViewPaginatedFilters } from 'components/DocRightRailV2/CollabItemsFilters/hooks/useCommentArticleViewPaginatedFilters'
import { useRootStore } from 'stores/RootStore'
import { RootState } from 'types'

import { CollaborationDialogAllCommentsHead } from './CollaborationDialogAllCommentsHead'
import { CollaborationDialogBody } from './CollaborationDialogBody'
import { CollaborationDialogComments } from './CollaborationDialogComments'
import { CollaborationDialogNewCommentHead } from './CollaborationDialogNewCommentHead'
import { CollaborationDialogSelectedCommentHead } from './CollaborationDialogSelectedCommentHead'
import {
  CollaborationDialogProvider,
  CollaborationDialogState,
  useCollaborationDialog,
} from './CollaborationDialogStore'
import { NewComment } from './NewComment'

import Styles from './CollaborationDialog.module.scss'
import { MoreThick } from 'almanac-ui/icons'

export const CollaborationDialog = observer(
  React.forwardRef<HTMLDivElement, {}>((_props, ref) => {
    return (
      <CollaborationDialogProvider>
        <CollaborationDialogBody ref={ref}>
          <CollaborationDialogContent />
        </CollaborationDialogBody>
      </CollaborationDialogProvider>
    )
  }),
)

const CollaborationDialogContent = observer(function CollaborationDialogContent() {
  const store = useCollaborationDialog()
  const rootStore = useRootStore()
  const editorView = useEditorView()

  // Go directly to new comment when have a selection from a fresh state
  useEffect(() => {
    if (
      editorView.state.selection.from !== editorView.state.selection.to &&
      store.currentState === CollaborationDialogState.AllComments
    ) {
      store.openNewComment()
    }
  }, [editorView, store])

  // If the selected comment is resolved, toggle the filters so we see resolved comments
  const linkedComment = computed(() =>
    rootStore.docStore?.comments.find((comment) => comment.id === rootStore.comments.selected?.id),
  ).get()
  useEffect(() => {
    if (linkedComment?.status === 'resolved') {
      rootStore.docStore?.filters.activate('resolved')
    } else if (linkedComment) {
      rootStore.docStore?.filters.deactivate('resolved')
    }
  }, [linkedComment, rootStore])

  // When something commented is selected toggle to selected state
  useEffect(
    () =>
      reaction(
        () => rootStore.comments.selected?.id,
        (commentId, prevId) => {
          if (commentId !== prevId && store.viewAll) {
            store.toggleViewAll()
          }
        },
      ),
    [rootStore, store],
  )

  // We don't want to cover the left rail on mobile
  const sidebarOpen = useSelector(({ floatingSidebar, sidebar }: RootState) => sidebar.locked || floatingSidebar)
  if (sidebarOpen) return null

  if (store.currentState === CollaborationDialogState.NewComment) {
    return (
      <>
        <CollaborationDialogNewCommentHead />
        <NewComment />
      </>
    )
  }

  if (store.currentState === CollaborationDialogState.SelectedComment) {
    return (
      <>
        <CollaborationDialogSelectedCommentHead />
        <CollaborationDialogComments />
      </>
    )
  }

  // store.currentState === CollaborationDialogState.AllComments
  return (
    <>
      <CollaborationDialogAllCommentsHead />
      <Filters />
      <CollaborationDialogComments />
    </>
  )
})

const Filters = observer(function Filters() {
  const { items, canExpand, canCollapse, toggle } = useCommentArticleViewPaginatedFilters({
    anticipateResolvedToggle: true,
  })
  const store = useCollaborationDialog()

  if (!store.showFilters) return null

  return (
    <div className={Styles.filterWrapper}>
      {items.map((filter) => (
        <CollabItemsFilter key={filter.key} filter={filter} />
      ))}
      {canExpand && (
        <Chip isSelected={false} icon={MoreThick} onPress={() => toggle(true)}>
          More
        </Chip>
      )}
      {canCollapse && (
        <Chip isSelected={false} icon={MoreThick} onPress={() => toggle(false)}>
          Less
        </Chip>
      )}
    </div>
  )
})
